<template>
  <v-card
    flat
    class="pa-3 mt-6"
  >
    <v-card-title class="d-flex align-center">
      <v-icon class="me-2">
        {{ icons.mdiShieldLockOutline }}
      </v-icon>
      المصادقة الثنائية
      <v-spacer></v-spacer>
      <v-chip
        v-if="state.enabled"
        color="success"
        small
      >
        مفعلة
      </v-chip>
      <v-chip
        v-else-if="state.required"
        color="warning"
        small
      >
        مطلوبة
      </v-chip>
      <v-chip
        v-else
        color="secondary"
        small
      >
        غير مفعلة
      </v-chip>
    </v-card-title>

    <v-card-text v-if="loading">
      <v-skeleton-loader type="text@3"></v-skeleton-loader>
    </v-card-text>

    <template v-else>
      <!-- already enrolled -->
      <v-card-text v-if="state.enabled">
        <p class="text-sm mb-2">
          يتم طلب رمز من تطبيق المصادقة عند كل عملية تسجيل دخول.
        </p>
        <p class="text-sm mb-1">
          تاريخ التفعيل: {{ state.confirmed_at | format_date }}
        </p>
        <p class="text-sm">
          رموز الاسترداد المتبقية: {{ state.recovery_codes_left }}
        </p>

        <v-btn
          color="primary"
          outlined
          small
          class="me-3 mt-2"
          @click="openPasswordDialog('regenerate')"
        >
          توليد رموز استرداد جديدة
        </v-btn>
        <v-btn
          v-if="!state.required"
          color="error"
          outlined
          small
          class="mt-2"
          @click="openPasswordDialog('disable')"
        >
          تعطيل
        </v-btn>
        <p
          v-else
          class="text-sm mt-3 mb-0 secondary--text"
        >
          المصادقة الثنائية مطلوبة من قبل ادارة النظام ولا يمكن تعطيلها.
        </p>
      </v-card-text>

      <!-- not enrolled: start / finish enrolment -->
      <v-card-text v-else>
        <p class="text-sm">
          تضيف المصادقة الثنائية خطوة اضافية عند تسجيل الدخول: رمز مؤقت من تطبيق مثل
          Google Authenticator او Microsoft Authenticator.
        </p>

        <v-btn
          color="primary"
          @click="setupDialog = true"
        >
          تفعيل المصادقة الثنائية
        </v-btn>
      </v-card-text>
    </template>

    <!-- enrolment: scan the QR, then prove the app is in sync -->
    <v-dialog
      v-model="setupDialog"
      max-width="460"
      persistent
    >
      <v-card>
        <v-card-title>
          تفعيل المصادقة الثنائية
        </v-card-title>
        <v-card-text>
          <totp-enrollment
            v-if="setupDialog"
            cancelable
            @enrolled="onEnrolled"
            @cancel="setupDialog = false"
            @failed="setupDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- password confirmation for disable / regenerate -->
    <v-dialog
      v-model="passwordDialog"
      max-width="420"
      persistent
    >
      <v-card>
        <v-card-title>
          {{ passwordAction === 'disable' ? 'تعطيل المصادقة الثنائية' : 'توليد رموز استرداد جديدة' }}
        </v-card-title>
        <v-card-text>
          <p class="text-sm">
            {{
              passwordAction === 'disable'
                ? 'يرجى تأكيد كلمة المرور لتعطيل المصادقة الثنائية.'
                : 'سيتم الغاء رموز الاسترداد الحالية واستبدالها برموز جديدة.'
            }}
          </p>
          <v-text-field
            v-model="password"
            label="كلمة المرور"
            type="password"
            autocomplete="current-password"
            dense
            outlined
            hide-details
            @keyup.enter="submitPasswordAction"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closePasswordDialog"
          >
            الغاء
          </v-btn>
          <v-btn
            :color="passwordAction === 'disable' ? 'error' : 'primary'"
            :loading="passwordLoading"
            :disabled="!password || passwordLoading"
            @click="submitPasswordAction"
          >
            تأكيد
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- recovery codes, shown once -->
    <v-dialog
      v-model="recoveryDialog"
      max-width="480"
      persistent
    >
      <v-card>
        <v-card-title>
          رموز الاسترداد
        </v-card-title>
        <v-card-text>
          <v-alert
            type="info"
            text
            dense
          >
            احفظ هذه الرموز في مكان آمن. تظهر مرة واحدة فقط، ويستخدم كل رمز لمرة واحدة عند فقدان تطبيق المصادقة.
          </v-alert>
          <div class="recovery-codes">
            <code
              v-for="rc in recoveryCodes"
              :key="rc"
              class="d-block py-1"
            >{{ rc }}</code>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            @click="copyRecoveryCodes"
          >
            <v-icon
              small
              class="me-1"
            >
              {{ icons.mdiContentCopy }}
            </v-icon>
            نسخ
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="closeRecoveryDialog"
          >
            تم الحفظ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mdiShieldLockOutline, mdiContentCopy } from '@mdi/js'
import axios from '@axios'
import toast from '@/store/toast'
import auth from '@/store/auth'
import TotpEnrollment from '@/components/TotpEnrollment.vue'

export default {
  name: 'TwoFactorCard',
  components: { TotpEnrollment },
  data: () => ({
    loading: true,
    state: {
      enabled: false,
      required: false,
      must_setup: false,
      confirmed_at: null,
      recovery_codes_left: 0,
    },
    setupDialog: false,
    passwordDialog: false,
    passwordAction: null,
    password: '',
    passwordLoading: false,
    recoveryDialog: false,
    recoveryCodes: [],
    icons: { mdiShieldLockOutline, mdiContentCopy },
  }),
  async mounted() {
    await this.fetchState()
  },
  methods: {
    async fetchState() {
      try {
        const { data: response } = await axios.get('/auth/totp')
        this.state = response.data
      } catch (error) {
        // the axios interceptor already surfaced the message
      } finally {
        this.loading = false
      }
    },
    async onEnrolled(payload) {
      this.state = payload.state
      this.recoveryCodes = payload.recovery_codes
      this.setupDialog = false
      this.recoveryDialog = true
      await toast.dispatch('success', 'تم تفعيل المصادقة الثنائية بنجاح.')
      await auth.dispatch('getUser')
    },
    openPasswordDialog(action) {
      this.passwordAction = action
      this.password = ''
      this.passwordDialog = true
    },
    closePasswordDialog() {
      this.passwordDialog = false
      this.password = ''
      this.passwordAction = null
      this.passwordLoading = false
    },
    async submitPasswordAction() {
      if (!this.password || this.passwordLoading) {
        return
      }
      this.passwordLoading = true
      const action = this.passwordAction
      try {
        if (action === 'disable') {
          const { data: response } = await axios.post('/auth/totp/disable', { password: this.password })
          this.state = response.data
          this.closePasswordDialog()
          await toast.dispatch('success', 'تم تعطيل المصادقة الثنائية.')
        } else {
          const { data: response } = await axios.post('/auth/totp/recovery-codes', { password: this.password })
          this.state = response.data.state
          this.recoveryCodes = response.data.recovery_codes
          this.closePasswordDialog()
          this.recoveryDialog = true
        }
        await auth.dispatch('getUser')
      } catch (error) {
        this.passwordLoading = false
      }
    },
    closeRecoveryDialog() {
      this.recoveryDialog = false
      this.recoveryCodes = []
    },
    async copyRecoveryCodes() {
      await this.copy(this.recoveryCodes.join('\n'), 'تم نسخ رموز الاسترداد.')
    },
    async copy(text, message) {
      try {
        await navigator.clipboard.writeText(text)
        await toast.dispatch('success', message)
      } catch (error) {
        await toast.dispatch('error', 'لم يتم النسخ، يرجى النسخ يدويا.')
      }
    },
  },
}
</script>

<style scoped>
.recovery-codes code {
  font-size: 0.95rem;
  letter-spacing: 1px;
}
.totp-secret ::v-deep input {
  letter-spacing: 2px;
}
</style>
